<template>
  <div class="online-exam-container">
    <!-- 页面头部 -->
    <div class="exam-header">
      <div class="exam-title">
        <h2>{{ homeworkName }}</h2>
        <p class="exam-info">在线答题</p>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧题型导航 -->
      <div class="question-nav">
        <div class="nav-header">
          <h3>题目导航</h3>
          <div class="nav-stats">
            <span>已完成: {{ completedCount }}/{{ questions.length }}</span>
          </div>
        </div>

        <div class="nav-sections">
          <!-- 选择题 -->
          <div v-if="choiceQuestions.length > 0" class="nav-section">
            <div class="section-title">
              <i class="el-icon-check"></i>
              选择题 ({{ choiceQuestions.length }}题)
            </div>
            <div class="question-numbers">
              <div
                v-for="question in choiceQuestions"
                :key="question.questionId || question.id"
                class="question-number-item"
                :class="{
                  'completed': isQuestionCompleted(question),
                  'current': currentQuestionIndex === getQuestionIndex(question)
                }"
                @click="scrollToQuestion(question)"
              >
                {{ getQuestionIndex(question) + 1 }}
              </div>
            </div>
          </div>

          <!-- 填空题 -->
          <div v-if="fillQuestions.length > 0" class="nav-section">
            <div class="section-title">
              <i class="el-icon-edit"></i>
              填空题 ({{ fillQuestions.length }}题)
            </div>
            <div class="question-numbers">
              <div
                v-for="question in fillQuestions"
                :key="question.questionId || question.id"
                class="question-number-item"
                :class="{
                  'completed': isQuestionCompleted(question),
                  'current': currentQuestionIndex === getQuestionIndex(question)
                }"
                @click="scrollToQuestion(question)"
              >
                {{ getQuestionIndex(question) + 1 }}
              </div>
            </div>
          </div>

          <!-- 判断题 -->
          <div v-if="judgeQuestions.length > 0" class="nav-section">
            <div class="section-title">
              <i class="el-icon-circle-check"></i>
              判断题 ({{ judgeQuestions.length }}题)
            </div>
            <div class="question-numbers">
              <div
                v-for="question in judgeQuestions"
                :key="question.questionId || question.id"
                class="question-number-item"
                :class="{
                  'completed': isQuestionCompleted(question),
                  'current': currentQuestionIndex === getQuestionIndex(question)
                }"
                @click="scrollToQuestion(question)"
              >
                {{ getQuestionIndex(question) + 1 }}
              </div>
            </div>
          </div>

          <!-- 简答题 -->
          <div v-if="subjectiveQuestions.length > 0" class="nav-section">
            <div class="section-title">
              <i class="el-icon-document"></i>
              简答题 ({{ subjectiveQuestions.length }}题)
            </div>
            <div class="question-numbers">
              <div
                v-for="question in subjectiveQuestions"
                :key="question.questionId || question.id"
                class="question-number-item"
                :class="{
                  'completed': isQuestionCompleted(question),
                  'current': currentQuestionIndex === getQuestionIndex(question)
                }"
                @click="scrollToQuestion(question)"
              >
                {{ getQuestionIndex(question) + 1 }}
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="nav-footer">
          <el-button type="primary" @click="submitExam" :loading="submitting" class="submit-btn">
            <i class="el-icon-check"></i> 提交答案
          </el-button>
          <el-button @click="goBack" class="back-btn">
            <i class="el-icon-back"></i> 返回
          </el-button>
        </div>
      </div>

      <!-- 右侧题目内容 -->
      <div class="questions-container" v-loading="loading">
      <div v-if="questions.length === 0" class="no-questions">
        <el-empty description="暂无题目"></el-empty>
      </div>

      <div v-else>
        <div
          v-for="(question, index) in questions"
          :key="question.questionId || question.id"
          :data-question-index="index"
          class="question-item"
        >
          <!-- 题目标题 -->
          <div class="question-header">
            <span class="question-number">第{{ index + 1 }}题</span>
            <span class="question-type">{{ getQuestionTypeName(question.quType) }}</span>
            <span class="question-score">{{ question.point || 5 }}分</span>
          </div>

          <!-- 题目内容 -->
          <div class="question-content">
            <div class="question-text" v-html="formatQuestionContent(question.content)"></div>

            <!-- 题目图片 -->
            <div v-if="question.image || question.url" class="question-image">
              <el-image
                :src="getImageUrl(question.image || question.url)"
                :preview-src-list="[getImageUrl(question.image || question.url)]"
                fit="contain"
              />
            </div>
          </div>

           <!-- 选择题选项 -->
           <div v-if="question.quType === 1 || question.quType === '1'" class="question-options">
             <div class="options-horizontal">
               <div v-for="(option, optIndex) in getQuestionOptions(question)" :key="optIndex" class="option-item-horizontal">
                 <div
                   class="option-box"
                   :class="{ 'selected': answers[question.questionId || question.id] === option.value }"
                   @click="selectOption(question, option.value)"
                 >
                 </div>
                 <span class="option-text">{{ option.text }}</span>
               </div>
             </div>
           </div>

          <!-- 填空题 -->
          <div v-else-if="question.quType === 2 || question.quType === '2'" class="question-fill">
            <div class="fill-blanks">
              <div v-for="(blank, blankIndex) in getFillBlanks(question.content)" :key="blankIndex" class="blank-item">
                <span v-if="blank.type === 'text'" v-html="blank.content"></span>
                <el-input
                  v-else-if="blank.type === 'blank'"
                  v-model="answers[question.questionId || question.id][blankIndex]"
                  :placeholder="`填空${blankIndex + 1}`"
                  class="blank-input"
                  @input="onAnswerChange"
                />
              </div>
            </div>
          </div>

          <!-- 判断题 -->
          <div v-else-if="question.quType === 3 || question.quType === '3'" class="question-judge">
            <el-radio-group v-model="answers[question.questionId || question.id]" @change="onAnswerChange">
              <el-radio label="true" class="judge-option">
                <i class="el-icon-check"></i> 正确
              </el-radio>
              <el-radio label="false" class="judge-option">
                <i class="el-icon-close"></i> 错误
              </el-radio>
            </el-radio-group>
          </div>

          <!-- 简答题/计算题/解答题 -->
          <div v-else-if="question.quType === 4 || question.quType === '4'" class="question-subjective">
            <div class="subjective-answer">
              <el-input
                v-model="answers[question.questionId || question.id]"
                type="textarea"
                :rows="4"
                placeholder="请输入您的答案..."
                @input="onAnswerChange"
              />
            </div>

            <!-- 图片上传区域 -->
            <div class="image-upload-section">
              <div class="upload-header">
                <span class="upload-title">上传作答图片</span>
                <el-button
                  type="text"
                  size="small"
                  @click="clearImage(question.questionId || question.id)"
                  v-if="answerImages[question.questionId || question.id]"
                >
                  清除图片
                </el-button>
              </div>

              <div class="upload-area">
                <el-upload
                  class="answer-uploader"
                  :action="uploadUrl"
                  :headers="uploadHeaders"
                  :show-file-list="false"
                  :on-success="(response, file) => handleImageSuccess(response, file, question.questionId || question.id)"
                  :on-error="handleImageError"
                  :before-upload="beforeImageUpload"
                  accept="image/*"
                >
                  <div v-if="answerImages[question.questionId || question.id]" class="image-preview">
                    <img
                      :src="answerImages[question.questionId || question.id]"
                      alt="作答图片"
                      @error="handleImageLoadError"
                      @load="console.log('图片加载成功:', answerImages[question.questionId || question.id])"
                    />
                    <div class="image-overlay">
                      <i class="el-icon-zoom-in"></i>
                      <span>点击重新上传</span>
                    </div>
                  </div>
                  <div v-else class="upload-placeholder">
                    <i class="el-icon-plus"></i>
                    <div class="upload-text">点击上传作答图片</div>
                    <div class="upload-tip">支持 JPG、PNG 格式</div>
                  </div>
                </el-upload>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- 提交确认对话框 -->
    <el-dialog
      title="确认提交"
      :visible.sync="submitDialogVisible"
      width="400px"
    >
      <div class="submit-confirm">
        <p>您确定要提交本次答题吗？</p>
        <p class="submit-tip">提交后将无法修改答案</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="submitDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSubmit" :loading="submitting">确认提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getSpecificHomeworkQuestions, uploadFile } from '@/api/homework'
import { submitHomework, getSubmitByStudentIdAndHomeworkId, getSubmitDetails } from '@/api/homeworkSubmit'
import { getToken } from '@/utils/auth'
import { parseJwt } from '@/utils/jwtUtils'

export default {
  name: 'OnlineExam',
  data() {
    return {
      homeworkId: null,
      homeworkName: '',
      questions: [],
      answers: {},
      answerImages: {},
      loading: false,
      submitting: false,
      submitDialogVisible: false,
      currentQuestionIndex: 0,
      uploadUrl: process.env.VUE_APP_BASE_API + '/questions/uploadImage',
      uploadHeaders: {
        'Authorization': 'Bearer ' + getToken()
      }
    }
  },
  computed: {
    // 选择题
    choiceQuestions() {
      return this.questions.filter(q => q.quType === 1 || q.quType === '1')
    },
    // 填空题
    fillQuestions() {
      return this.questions.filter(q => q.quType === 2 || q.quType === '2')
    },
    // 判断题
    judgeQuestions() {
      return this.questions.filter(q => q.quType === 3 || q.quType === '3')
    },
    // 简答题
    subjectiveQuestions() {
      return this.questions.filter(q => q.quType === 4 || q.quType === '4')
    },
    // 已完成题目数量
    completedCount() {
      return this.questions.filter(q => this.isQuestionCompleted(q)).length
    }
  },
  created() {
    this.homeworkId = this.$route.params.homeworkId
    this.homeworkName = this.$route.query.name || '作业'
    this.loadQuestions()
  },
  mounted() {
    // 添加滚动监听
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    // 移除滚动监听
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    // 加载题目
    async loadQuestions() {
      this.loading = true
      try {
        const response = await getSpecificHomeworkQuestions(this.homeworkId)
        console.log('获取题目响应:', response)
        if (response.code === 1) {
          // 按题型排序：选择题、填空题、判断题、简答题
          this.questions = this.sortQuestionsByType(response.data)
          console.log('题目数据:', this.questions)

          // 打印每个题目的详细信息，特别是选择题
          this.questions.forEach((question, index) => {
            console.log(`题目 ${index + 1}:`, {
              id: question.questionId || question.id,
              type: question.quType,
              content: question.content,
              optionsStr: question.optionsStr,
              options: question.options
            })
          })

          // 初始化答案并加载已提交的答案
          this.initializeAnswers()
          await this.loadExistingAnswers()
        } else {
          this.$message.error(response.msg || '加载题目失败')
        }
      } catch (error) {
        console.error('加载题目失败:', error)
        this.$message.error('加载题目失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 初始化答案对象
    initializeAnswers() {
      this.questions.forEach(question => {
        const questionId = question.questionId || question.id
        if (question.quType === 2 || question.quType === '2') {
          // 填空题初始化为数组
          this.$set(this.answers, questionId, [])
        } else {
          // 其他题型（选择题、判断题、简答题）初始化为空字符串
          this.$set(this.answers, questionId, '')
        }
      })
      console.log('初始化后的答案对象:', this.answers)
    },

    // 加载已提交的答案
    async loadExistingAnswers() {
      try {
        // 获取用户ID
        const token = getToken()
        const userInfo = parseJwt(token)
        const userId = JSON.parse(userInfo.userInfo).id

        // 通过用户ID查询学生信息，获取student_id
        const studentResponse = await fetch(`/api/student/user/${userId}`)
        const studentData = await studentResponse.json()

        if (studentData.code !== 1 || !studentData.data) {
          console.log('未找到学生信息，跳过加载已有答案')
          return
        }

        const studentId = studentData.data.studentId
        console.log('通过用户ID查询到学生ID:', userId, '->', studentId)

        // 获取学生的作业提交记录
        const response = await getSubmitByStudentIdAndHomeworkId(studentId, this.homeworkId)
        console.log('获取提交记录响应:', response)

        if (response.code === 1 && response.data) {
          const submitRecord = response.data
          console.log('找到提交记录:', submitRecord)

          // 获取提交明细
          const detailsResponse = await getSubmitDetails(submitRecord.submitId)
          console.log('获取提交明细响应:', detailsResponse)

          if (detailsResponse.code === 1 && detailsResponse.data) {
            const details = detailsResponse.data
            console.log('提交明细:', details)

            // 将已提交的答案填充到答案对象中
            details.forEach(detail => {
              const questionId = detail.questionId
              if (detail.answerText) {
                if (this.answers[questionId] !== undefined) {
                  // 根据题型处理答案
                  const question = this.questions.find(q => (q.questionId || q.id) === questionId)
                  if (question) {
                    if (question.quType === 2 || question.quType === '2') {
                      // 填空题：将答案按分隔符分割成数组
                      this.$set(this.answers, questionId, detail.answerText.split('|'))
                    } else {
                      // 其他题型：直接设置答案
                      this.$set(this.answers, questionId, detail.answerText)
                    }
                  }
                }
              }
              if (detail.answerImageUrl) {
                this.$set(this.answerImages, questionId, detail.answerImageUrl)
              }
            })

            console.log('加载已提交答案后的答案对象:', this.answers)
            console.log('加载已提交答案后的图片对象:', this.answerImages)

            // 显示提示信息
            this.$message.success('已加载之前提交的答案')
          }
        } else {
          console.log('未找到已提交的答案')
        }
      } catch (error) {
        console.error('加载已提交答案失败:', error)
        // 不显示错误信息，因为可能是第一次答题
      }
    },

    // 按题型排序题目
    sortQuestionsByType(questions) {
      const typeOrder = {
        1: 1, // 选择题
        2: 2, // 填空题
        3: 3, // 判断题
        4: 4 // 简答题
      }

      return questions.sort((a, b) => {
        const typeA = parseInt(a.quType) || a.quType
        const typeB = parseInt(b.quType) || b.quType

        const orderA = typeOrder[typeA] || 999
        const orderB = typeOrder[typeB] || 999

        if (orderA !== orderB) {
          return orderA - orderB
        }

        // 如果题型相同，按ID排序
        return (a.questionId || a.id) - (b.questionId || b.id)
      })
    },

    // 判断题目是否已完成
    isQuestionCompleted(question) {
      const questionId = question.questionId || question.id
      const answer = this.answers[questionId]

      if (question.quType === 2 || question.quType === '2') {
        // 填空题：检查数组是否有非空值
        return Array.isArray(answer) && answer.some(item => item && item.trim() !== '')
      } else {
        // 其他题型：检查是否有答案
        return answer && answer.toString().trim() !== ''
      }
    },

    // 获取题目在总列表中的索引
    getQuestionIndex(question) {
      return this.questions.findIndex(q =>
        (q.questionId || q.id) === (question.questionId || question.id)
      )
    },

    // 滚动到指定题目
    scrollToQuestion(question) {
      const index = this.getQuestionIndex(question)
      if (index !== -1) {
        this.currentQuestionIndex = index
        const questionElement = document.querySelector(`[data-question-index="${index}"]`)
        if (questionElement) {
          questionElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    },

    // 处理滚动事件
    handleScroll() {
      const questionElements = document.querySelectorAll('[data-question-index]')
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight

      let currentIndex = 0
      questionElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect()
        if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
          currentIndex = index
        }
      })

      this.currentQuestionIndex = currentIndex
    },

    // 获取题目类型名称
    getQuestionTypeName(quType) {
      const typeMap = {
        1: '选择题',
        2: '填空题',
        3: '判断题',
        4: '简答题'
      }
      return typeMap[quType] || '未知题型'
    },

    // 格式化题目内容
    formatQuestionContent(content) {
      if (!content) return ''
      // 处理换行符
      return content.replace(/\n/g, '<br>')
    },

    // 获取图片URL（支持绝对、/api/file/、以及 /{bucket}/{key}）
    getImageUrl(image) {
      if (!image) return ''
      const s = String(image)
      if (/^(https?:)?\/\//i.test(s)) return s
      if (s.startsWith('/api/file/')) return s
      if (/^\/[A-Za-z0-9_-]+\//.test(s)) {
        try {
          const { getMinioUrl } = require('@/utils/minio')
          return getMinioUrl(s)
        } catch (_) {}
      }
      const base = process.env.VUE_APP_BASE_API || ''
      return (base ? (base.replace(/\/$/, '') + '/') : '') + s.replace(/^\//, '')
    },

    // 清理选项文本，移除"选项"前缀
    cleanOptionText(text) {
      if (!text) return text
      // 移除"选项A"、"选项B"等前缀
      return text.replace(/^选项[A-Z]\s*/, '').trim()
    },

    // 获取选择题选项
    getQuestionOptions(question) {
      console.log('获取选择题选项，题目数据:', question)
      console.log('options字段:', question.options)
      console.log('optionsStr字段:', question.optionsStr)

      // 优先使用 options 字段（如果存在）
      if (question.options) {
        console.log('使用 options 字段:', question.options, '类型:', typeof question.options)

        // 如果 options 是字符串，尝试解析为JSON数组
        if (typeof question.options === 'string') {
          try {
            // 尝试直接解析JSON
            const parsedOptions = JSON.parse(question.options)
            console.log('直接解析成功:', parsedOptions)
            if (Array.isArray(parsedOptions)) {
              const result = parsedOptions.map((option, index) => ({
                label: String.fromCharCode(65 + index), // A, B, C, D
                value: String.fromCharCode(65 + index),
                text: this.cleanOptionText(option.content || option.text || option)
              }))
              console.log('解析后的选项:', result)
              return result
            }
          } catch (error) {
            console.log('直接解析失败，尝试添加方括号:', error)
            try {
              // 尝试添加方括号后解析
              const parsedOptions = JSON.parse('[' + question.options + ']')
              console.log('添加方括号解析成功:', parsedOptions)
              if (Array.isArray(parsedOptions)) {
                const result = parsedOptions.map((option, index) => ({
                  label: String.fromCharCode(65 + index), // A, B, C, D
                  value: String.fromCharCode(65 + index),
                  text: this.cleanOptionText(option.content || option.text || option)
                }))
                console.log('解析后的选项:', result)
                return result
              }
            } catch (error2) {
              console.error('添加方括号解析也失败:', error2)
              // 如果JSON解析都失败，尝试按逗号分割
              const options = question.options.split(',').map(opt => opt.trim()).filter(opt => opt)
              console.log('按逗号分割的选项:', options)
              if (options.length > 0) {
                const result = options.map((option, index) => ({
                  label: String.fromCharCode(65 + index),
                  value: String.fromCharCode(65 + index),
                  text: this.cleanOptionText(option)
                }))
                console.log('分割后的选项:', result)
                return result
              }
            }
          }
        }

        // 如果 options 是数组
        if (Array.isArray(question.options)) {
          console.log('options是数组:', question.options)
          const result = question.options.map((option, index) => ({
            label: String.fromCharCode(65 + index), // A, B, C, D
            value: String.fromCharCode(65 + index),
            text: this.cleanOptionText(option.content || option.text || option)
          }))
          console.log('数组解析后的选项:', result)
          return result
        }
      }

      // 尝试解析 optionsStr 字段
      if (question.optionsStr) {
        try {
          console.log('尝试解析 optionsStr:', question.optionsStr)
          const options = JSON.parse(question.optionsStr)
          console.log('解析后的选项:', options)

          if (Array.isArray(options)) {
            return options.map((option, index) => ({
              label: String.fromCharCode(65 + index), // A, B, C, D
              value: String.fromCharCode(65 + index),
              text: this.cleanOptionText(option.content || option.text || option)
            }))
          }
        } catch (error) {
          console.error('解析 optionsStr 失败:', error)
          console.log('原始 optionsStr 内容:', question.optionsStr)

          // 如果JSON解析失败，尝试其他格式
          if (typeof question.optionsStr === 'string') {
            // 尝试按逗号分割
            const options = question.optionsStr.split(',').map(opt => opt.trim()).filter(opt => opt)
            if (options.length > 0) {
              return options.map((option, index) => ({
                label: String.fromCharCode(65 + index),
                value: String.fromCharCode(65 + index),
                text: option
              }))
            }
          }
        }
      }

      // 如果都没有找到选项数据，返回默认选项
      console.warn('无法解析选项数据，使用默认选项')
      return [
        { label: 'A', value: 'A', text: 'A' },
        { label: 'B', value: 'B', text: 'B' },
        { label: 'C', value: 'C', text: 'C' },
        { label: 'D', value: 'D', text: 'D' }
      ]
    },

    // 获取填空题的空格
    getFillBlanks(content) {
      if (!content) return []

      const blanks = []
      const parts = content.split(/(____|_+)/)

      parts.forEach((part, index) => {
        if (part.match(/^_+$/)) {
          blanks.push({ type: 'blank', content: part })
        } else if (part.trim()) {
          blanks.push({ type: 'text', content: part })
        }
      })

      return blanks
    },

     // 选择选项
     selectOption(question, value) {
       const questionId = question.questionId || question.id
       this.$set(this.answers, questionId, value)
       this.onAnswerChange()
     },

     // 答案变化处理
     onAnswerChange() {
       // 可以在这里添加自动保存逻辑
       console.log('答案已更新:', this.answers)
     },

    // 图片上传前验证
    beforeImageUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!')
        return false
      }
      return true
    },

    // 图片上传成功
    handleImageSuccess(response, file, questionId) {
      console.log('图片上传成功响应:', response)
      console.log('题目ID:', questionId)

      if (response.code === 1) {
        // 直接使用后端返回的完整URL，或者构建正确的URL
        let imageUrl = response.data || response.msg
        console.log('原始图片URL:', imageUrl)

        // 如果返回的不是完整URL，则构建完整URL
        if (imageUrl && !imageUrl.startsWith('http')) {
          // 后端返回的是MinIO的相对路径，需要构建MinIO的完整URL
          imageUrl = `http://localhost:9000${imageUrl}`
        }

        console.log('最终图片URL:', imageUrl)
        this.$set(this.answerImages, questionId, imageUrl)
        console.log('设置后的answerImages:', this.answerImages)
        this.$message.success('图片上传成功')
      } else {
        console.error('图片上传失败:', response)
        this.$message.error(response.msg || '图片上传失败')
      }
    },

    // 图片上传失败
    handleImageError(error) {
      console.error('图片上传失败:', error)
      this.$message.error('图片上传失败，请重试')
    },

    // 清除图片
    clearImage(questionId) {
      this.$delete(this.answerImages, questionId)
      this.$message.success('图片已清除')
    },

    // 图片加载错误
    handleImageLoadError(event) {
      console.error('图片加载失败:', event.target.src)
      event.target.style.display = 'none'
      this.$message.error('图片加载失败，请重新上传')
    },

    // 提交答题
    submitExam() {
      this.submitDialogVisible = true
    },

    // 确认提交
    async confirmSubmit() {
      this.submitting = true
      try {
        // 获取用户ID
        const token = getToken()
        const userInfo = parseJwt(token)
        const userId = JSON.parse(userInfo.userInfo).id

        // 通过用户ID查询学生信息，获取student_id
        const studentResponse = await fetch(`/api/student/user/${userId}`)
        const studentData = await studentResponse.json()

        if (studentData.code !== 1 || !studentData.data) {
          this.$message.error('未找到学生信息，无法提交作业')
          return
        }

        const studentId = studentData.data.studentId
        console.log('提交作业 - 通过用户ID查询到学生ID:', userId, '->', studentId)

        // 构建提交数据
        const submitData = {
          studentId: parseInt(studentId),
          homeworkId: parseInt(this.homeworkId),
          answerImageUrl: null, // 如果有整体图片可以设置
          details: []
        }

        // 处理每道题的答案
        this.questions.forEach(question => {
          const questionId = question.questionId || question.id
          const answer = this.answers[questionId]
          const answerImage = this.answerImages[questionId]

          if (answer || answerImage) {
            let answerText = ''

            // 根据题型处理答案
            if (question.quType === 2 || question.quType === '2') {
              // 填空题：将数组用|分隔符连接
              if (Array.isArray(answer)) {
                answerText = answer.join('|')
              } else {
                answerText = answer || ''
              }
            } else {
              // 其他题型：直接使用答案
              answerText = answer || ''
            }

            const detail = {
              questionId: questionId,
              answerText: answerText,
              answerImageUrl: answerImage || null,
              questionScore: question.point || 5
            }
            submitData.details.push(detail)
          }
        })

        console.log('提交数据:', submitData)

        // 调用提交API
        const response = await submitHomework(submitData)
        if (response.code === 1) {
          this.$message.success('作业提交成功！')
          this.submitDialogVisible = false

          // 延迟返回上一页
          setTimeout(() => {
            this.goBack()
          }, 1500)
        } else {
          this.$message.error(response.msg || '提交失败')
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },

    // 返回作业汇总页面
    goBack() {
      // 获取课程ID
      const courseId = this.$route.query.courseId || this.$store.getters.courseId
      console.log('返回作业汇总页面 - 课程ID:', courseId)

      if (!courseId) {
        this.$message.error('无法获取课程ID，请重新进入课程页面')
        return
      }

      // 返回到课程详情页面的作业汇总部分
      this.$router.push({
        name: 'CourseDetail',
        params: {
          id: courseId // 使用id作为参数名，因为路由定义中使用的是:id
        },
        query: {
          tab: 'student-homework-summary' // 指定显示学生作业汇总标签页
        }
      })
    }
  }
}
</script>

<style scoped>
.online-exam-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.exam-header {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.exam-title h2 {
  margin: 0 0 5px 0;
  color: #303133;
  font-size: 24px;
}

.exam-info {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

/* 主要内容区域 */
.main-content {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

/* 左侧导航区域 */
.question-nav {
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.nav-header {
  padding: 20px;
  border-bottom: 1px solid #ebeef5;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.nav-header h3 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 16px;
  font-weight: bold;
}

.nav-stats {
  font-size: 14px;
  color: #67c23a;
  font-weight: bold;
}

.nav-sections {
  padding: 15px;
}

.nav-section {
  margin-bottom: 20px;
}

.nav-section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #f0f2f5;
  border-radius: 4px;
}

.section-title i {
  margin-right: 8px;
  color: #409eff;
}

.question-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.question-number-item {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f5f7fa;
  color: #909399;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.question-number-item:hover {
  background: #e6f7ff;
  color: #409eff;
  border-color: #409eff;
}

.question-number-item.completed {
  background: #67c23a;
  color: white;
}

.question-number-item.current {
  background: #409eff;
  color: white;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* 底部操作按钮 */
.nav-footer {
  padding: 20px;
  border-top: 1px solid #ebeef5;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.nav-footer .submit-btn {
  width: 100%;
  margin-bottom: 10px;
  height: 40px;
  font-size: 16px;
  font-weight: bold;
  padding: 0;
  line-height: 40px;
  box-sizing: border-box;
}

.nav-footer .back-btn {
  width: 100%;
  height: 40px;
  font-size: 16px;
  padding: 0;
  line-height: 40px;
  box-sizing: border-box;
}

/* 右侧题目容器 */
.questions-container {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.question-item {
  padding: 30px;
  border-bottom: 1px solid #ebeef5;
}

.question-item:last-child {
  border-bottom: none;
}

.question-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
}

.question-number {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}

.question-type {
  background: #e1f3d8;
  color: #67c23a;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
}

.question-score {
  background: #fdf6ec;
  color: #e6a23c;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
}

.question-content {
  margin-bottom: 20px;
}

.question-text {
  font-size: 16px;
  line-height: 1.6;
  color: #303133;
  margin-bottom: 15px;
}

.question-image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.question-options {
  margin-top: 20px;
}

.options-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.option-item-horizontal {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.option-item-horizontal:hover .option-box {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.option-box {
  width: 32px;
  height: 32px;
  border: 2px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  cursor: pointer;
  position: relative;
}

.option-box.selected {
  background: #409eff;
  border-color: #409eff;
  color: white;
}

.option-box.selected::after {
  content: '✓';
  color: white;
  font-weight: bold;
  font-size: 16px;
}

.option-text {
  color: #303133;
  font-size: 15px;
  line-height: 1.5;
}

.question-fill {
  margin-top: 20px;
}

.fill-blanks {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.blank-item {
  display: inline-block;
}

.blank-input {
  width: 120px;
  margin: 0 5px;
}

.question-judge {
  margin-top: 20px;
}

.judge-option {
  margin-right: 30px;
  font-size: 16px;
}

.question-subjective {
  margin-top: 20px;
}

.subjective-answer {
  margin-bottom: 20px;
}

.image-upload-section {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 20px;
  background: #fafafa;
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.upload-title {
  font-weight: bold;
  color: #303133;
}

.upload-area {
  text-align: center;
}

.answer-uploader {
  display: inline-block;
}

.image-preview {
  position: relative;
  display: inline-block;
}

.image-preview img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.upload-placeholder {
  width: 200px;
  height: 200px;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #8c939d;
  cursor: pointer;
  transition: border-color 0.3s;
}

.upload-placeholder:hover {
  border-color: #409eff;
}

.upload-placeholder i {
  font-size: 28px;
  margin-bottom: 10px;
}

.upload-text {
  font-size: 14px;
  margin-bottom: 5px;
}

.upload-tip {
  font-size: 12px;
  color: #c0c4cc;
}

.submit-confirm {
  text-align: center;
  padding: 20px 0;
}

.submit-tip {
  color: #909399;
  font-size: 14px;
  margin-top: 10px;
}

.no-questions {
  padding: 60px 20px;
  text-align: center;
}

@media (max-width: 768px) {
  .online-exam-container {
    padding: 10px;
  }

  .exam-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .main-content {
    flex-direction: column;
  }

  .question-nav {
    width: 100%;
    position: static;
    max-height: none;
    margin-bottom: 20px;
  }

  .question-numbers {
    justify-content: center;
  }

  .nav-footer {
    padding: 15px;
  }

  .nav-footer .submit-btn,
  .nav-footer .back-btn {
    height: 44px;
    font-size: 16px;
    padding: 0;
    line-height: 44px;
    box-sizing: border-box;
  }

  .question-item {
    padding: 20px;
  }

  .question-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .options-horizontal {
    gap: 15px;
  }

  .option-item-horizontal {
    gap: 6px;
  }

  .option-box {
    width: 28px;
    height: 28px;
  }

  .option-label {
    font-size: 12px;
  }

  .option-text {
    font-size: 14px;
  }
}
</style>
